import React, { useEffect, useMemo, useState } from "react";
import { DashboardFilters, DashboardFiltersBar } from "../../components/DashboardFiltersBar";
import { useAuth } from "../../auth/AuthContext";

type SummaryPayload = {
  total: number;
  byEvent: Array<{ event: string; _count: number }>;
  byUser: Array<{
    userId: number | null;
    count: number;
    user?: { id: number; name: string; email: string } | null;
  }>;
  byResource: Array<{ resourceType: string | null; _count: number }>;
};

export const AuditSummaryPage: React.FC = () => {
  const { client } = useAuth();
  const [filters, setFilters] = useState<DashboardFilters>({});
  const [data, setData] = useState<SummaryPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.entityId) {
      params.set("entityId", String(filters.entityId));
    }
    if (filters.from) {
      params.set("from", filters.from);
    }
    if (filters.to) {
      params.set("to", filters.to);
    }
    return params.toString();
  }, [filters]);

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      setError(null);
      const response = await client.get(`/audit/summary?${query}`);
      if (!response.ok) {
        console.error("Audit summary failed", { status: response.status });
        setData(null);
        setError("Falha ao carregar resumo.");
        setLoading(false);
        return;
      }
      setData((await response.json()) as SummaryPayload);
      setLoading(false);
    };
    fetchSummary();
  }, [client, query]);

  return (
    <div className="container py-4">
      <h1 className="mb-3">Resumo de Auditoria</h1>
      <DashboardFiltersBar value={filters} onChange={setFilters} />
      {loading && <div className="alert alert-info">Carregando...</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="row g-3">
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h6 className="text-muted">Total de eventos</h6>
              <h3>{data?.total ?? 0}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mt-1">
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title">Top eventos</h5>
              <ul className="list-unstyled mb-0">
                {(data?.byEvent ?? []).map((item) => (
                  <li key={item.event} className="d-flex justify-content-between">
                    <span>{item.event}</span>
                    <span>{item._count}</span>
                  </li>
                ))}
                {(!data?.byEvent || data.byEvent.length === 0) && (
                  <li className="text-muted">Sem dados</li>
                )}
              </ul>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title">Top usuarios</h5>
              <ul className="list-unstyled mb-0">
                {(data?.byUser ?? []).map((item) => (
                  <li key={item.userId ?? "unknown"} className="d-flex justify-content-between">
                    <span>{item.user?.name ?? "Sem usuario"}</span>
                    <span>{item.count}</span>
                  </li>
                ))}
                {(!data?.byUser || data.byUser.length === 0) && (
                  <li className="text-muted">Sem dados</li>
                )}
              </ul>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title">Recursos alterados</h5>
              <ul className="list-unstyled mb-0">
                {(data?.byResource ?? []).map((item) => (
                  <li
                    key={item.resourceType ?? "unknown"}
                    className="d-flex justify-content-between"
                  >
                    <span>{item.resourceType ?? "Sem recurso"}</span>
                    <span>{item._count}</span>
                  </li>
                ))}
                {(!data?.byResource || data.byResource.length === 0) && (
                  <li className="text-muted">Sem dados</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
